import { Injectable, inject } from '@angular/core';
import { GeoBrowserService, InteractionService, MapColors } from '@next/arable-lib/geo';
import { CoreActions } from '@next/core-lib';
import { Store, select } from '@ngrx/store';

import { EventsKey } from 'ol/events';
import { GeoJSON } from 'ol/format';
import GeometryType from 'ol/geom/GeometryType';
import Draw, { DrawEvent, DrawEventType } from 'ol/interaction/Draw';
import { Vector as Layer } from 'ol/layer';
import { unByKey } from 'ol/Observable';
import { Vector } from 'ol/source';
import { Stroke, Style, Fill } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { SiteState } from 'src/app/shared/models';

import { InteractionType } from '../../models';
import { Feature as LocalFeature } from '../../models';

import { InteractionFeatureDrawn } from '../../store/actions/feature.actions';
import { getSelectedFeaturesIds } from '../../store/selectors/feature.selectors';

@Injectable({
  providedIn: 'root',
})
export class GeoDrawService implements InteractionService {
  readonly store = inject<Store<SiteState>>(Store);

  drawstartEventsKey: EventsKey;
  drawendEventsKey: EventsKey;
  interactionTypes = [InteractionType.drawFeature];

  readonly source = new Vector({
    wrapX: false,
  });

  readonly layer = new Layer({
    source: this.source,
    zIndex: 10,
    style: new Style({
      stroke: new Stroke({
        color: MapColors.modifyStroke,
        width: 2.5,
      }),
      fill: new Fill({
        color: MapColors.modifyFill,
      }),
    }),
  });

  readonly draw = new Draw({
    source: this.source,
    type: GeometryType.POLYGON,
    style: new Style({
      stroke: new Stroke({
        color: MapColors.drawStroke,
        width: 2.5,
      }),
      fill: new Fill({
        color: MapColors.drawFill,
      }),
      image: new CircleStyle({
        radius: 7,
        stroke: new Stroke({
          color: MapColors.drawCursorStroke,
          width: 2.5,
        }),
        fill: new Fill({
          color: MapColors.drawCursorFill,
        }),
      }),
    }),
  });

  init(geoService: GeoBrowserService) {
    this.store.pipe(select(getSelectedFeaturesIds)).subscribe((selectedFeatureIds) => {
      if (!selectedFeatureIds || selectedFeatureIds.length === 0) {
        this.source.clear();
      }
    });

    this.draw.setActive(false);
    geoService.map.addLayer(this.layer);
    geoService.map.addInteraction(this.draw);
  }

  destroy(geoService: GeoBrowserService) {
    this.layer.dispose();
    geoService.map.removeLayer(this.layer);
    geoService.map.removeInteraction(this.draw);
  }

  start(_: GeoBrowserService) {
    this.draw.setActive(true);
    this.layer.setVisible(true);
    this.drawendEventsKey = this.draw.on('drawend' as DrawEventType.DRAWEND, (e: DrawEvent) => {
      this.store.dispatch(CoreActions.setLoading({ loading: true }));

      setTimeout(() => {
        this.store.dispatch(CoreActions.setLoading({ loading: false }));
        this.store.dispatch(
          new InteractionFeatureDrawn(new GeoJSON().writeFeatureObject(e.feature) as LocalFeature),
        );
      }, 0);
    });
  }

  stop(_: GeoBrowserService) {
    this.draw.setActive(false);
    this.source.clear();
    this.layer.setVisible(false);
    unByKey(this.drawstartEventsKey);
    unByKey(this.drawendEventsKey);
  }
}
